"use client";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ThumbDownOffAltIcon from "@mui/icons-material/ThumbDownOffAlt";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import { motion } from "framer-motion";
import { useState } from "react";
import { customerOrdersService } from "~/connectors/orders.connector";
import { songSharingService } from "~/connectors/songSharing.connector";
import type { Song } from "~/types";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";

type Props = { song: Song };

// Helper to create the floating mini notes
const Particle = ({ angle, color }: { angle: number; color: string }) => {
  const distance = 35;
  const rad = (angle * Math.PI) / 180;
  const x = Math.cos(rad) * distance;
  const y = Math.sin(rad) * distance;

  return (
    <motion.div
      initial={{ x: 0, y: 0, opacity: 1, scale: 0.2 }}
      animate={{ x, y, opacity: 0, scale: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      style={{ position: "absolute", color, zIndex: 10, pointerEvents: "none" }}
    >
      <MusicNoteIcon fontSize="inherit" />
    </motion.div>
  );
};

export default function ReviewThumbsButtonsAction({ song }: Props) {
  return <ReviewThumbsButtons song={song} key={song.review?.rating} />;
}

function ReviewThumbsButtons({ song }: Props) {
  const initial = song.review?.rating ?? null;
  const [rating, setRating] = useState<number | null>(initial);
  const [animPhase, setAnimPhase] = useState<"idle" | "celebrating">("idle");

  const submit = async (newRating: number) => {
    const prev = rating;

    let isRemovingReview = false;
    if (prev === newRating) {
      isRemovingReview = true;
      setRating(null);
    } else {
      setRating(newRating);
    }

    if (!isRemovingReview && newRating === 5) {
      setAnimPhase("celebrating");
      // Reset exactly after 0.5s to show the filled thumb
      setTimeout(() => setAnimPhase("idle"), 500);
    }

    try {
      if (song.isShared) {
        await songSharingService.submitReview(song.orderId!, {
          rating: newRating,
        });
      } else {
        await customerOrdersService.submitReview(song.orderId!, {
          rating: newRating,
        });
      }
    } catch (err) {
      setRating(prev);
      setAnimPhase("idle");
    }
  };

  const onThumbUp = () => {
    if (!song.orderId) return;
    submit(5);
  };

  const onThumbDown = () => {
    if (!song.orderId) return;
    submit(1);
  };

  return (
    <ButtonGroup variant="audio" aria-label="review-thumbs">
      <Button onClick={onThumbUp} style={{ overflow: "visible" }}>
        {/* Fixed Box prevents layout jumps */}
        <Box
          sx={{
            position: "relative",
            width: 24,
            height: 24,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}>
          {/* Particles shoot out when celebrating */}
          {animPhase === "celebrating" && (
            <>
              <Particle angle={-60} color="#FF69B4" />
              <Particle angle={-20} color="#00CED1" />
              <Particle angle={20} color="#FFD700" />
              <Particle angle={60} color="#32CD32" />
            </>
          )}

          {/* Main Icon Animation */}
          <motion.div
            animate={
              animPhase === "celebrating"
                ? {
                  scale: [1, 1.4, 1], // Scale up and down
                  rotate: [0, -15, 15, 0], // Vibrate
                }
                : { scale: 1, rotate: 0 }
            }
            transition={{
              duration: 0.5,
              ease: "easeOut",
            }}
            style={{ display: "flex" }}
          >
            {animPhase === "celebrating" ? (
              <MusicNoteIcon />
            ) : rating === 5 ? (
              <ThumbUpIcon />
            ) : (
              <ThumbUpOffAltIcon />
            )}
          </motion.div>
        </Box>
      </Button>
      <Button onClick={onThumbDown}>
        <Box
          sx={{
            width: 24,
            height: 24,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}>
          {rating === 1 ? <ThumbDownIcon /> : <ThumbDownOffAltIcon />}
        </Box>
      </Button>
    </ButtonGroup>
  );
}
